import React, { Component } from "react";
import PropTypes from "prop-types";
import Icon from "./../Icon";
import Calendar from "./../Calendar";

import "./datePicker.scss";
import Button from "../Button";
import { DATE_FORMAT_GROUPS, DATE_REGEX } from "../../const/datePickerConstants";

export default class DatePicker extends Component {
  constructor(props) {
    super(props);

    const initialDateValue = this.props.datePickerValue || "";
    const initialDateObj = initialDateValue 
      ? _parseDateByLocale(initialDateValue, this.props.locale)
      : null;
    const isValidDate = initialDateObj && !isNaN(initialDateObj.getTime());

    this.state = {
      datepickerValue: initialDateValue,
      dateObject: isValidDate ? initialDateObj : null,
      inputState: this.props.inputState || "",
    };
    this.applyDatePickerStyles = _applyDatePickerStyles.bind(this);
    this.datePickerOpen = _datePickerOpen.bind(this);
    this.calendarHandler = _calendarHandler.bind(this);
    this.changeHandler = _changeHandler.bind(this);
    this.parseDate = _parseDate.bind(this);
  }

  componentDidMount() {
    this.applyDatePickerStyles(this.props.inputState);
    document.addEventListener("click", this.clickListener);
  }

  componentWillReceiveProps(nextProps) {
    this.applyDatePickerStyles(nextProps.inputState);
    
    // Update dateObject when datePickerValue prop changes
    if (nextProps.datePickerValue !== this.props.datePickerValue) {
      const dateValue = nextProps.datePickerValue || "";
      const dateObj = dateValue 
        ? _parseDateByLocale(dateValue, nextProps.locale)
        : null;
      const isValidDate = dateObj && !isNaN(dateObj.getTime());
      
      this.setState({
        datepickerValue: dateValue,
        dateObject: isValidDate ? dateObj : null,
      });
    }
  }
  
  componentDidUpdate(prevProps, prevState) {
    // Only update when props change
    if (prevProps.inputState !== this.props.inputState) {
      this.setState({ inputState: this.props.inputState });
      return; // IMPORTANT: This stops further execution leads to loop
    }
    // Only apply styles when state changes
    if (prevState.inputState !== this.state.inputState) {
      this.applyDatePickerStyles(this.state.inputState);
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.clickListener);
  }

  clickListener = (e) => {
    const currentElement = e.target;

    if (!this.container.contains(currentElement)) {
      this.setState({ displayOpen: false });
    }
  };

  handleKeys = (e) => {
    if (e.which === 27) {
      this.setState({ displayOpen: false });
      this.input.focus();
    }
    if (e.altKey && e.which === 40) {
      this.setState({
        displayOpen: true,
      });
    }
  };

  focusCheck = (e) => {
    if (
      document.activeElement === this.cal.children[0].children[0].children[2]
    ) {
      if (!e.shiftKey && e.which === 9) {
        this.setState({ displayOpen: false });
      }
    }
    if (
      document.activeElement ===
      this.cal.children[0].children[0].children[0].children[1]
    ) {
      if (e.shiftKey && e.which === 9) {
        this.setState({ displayOpen: false });
      }
    }
  };

  render() {
    const {
      inputStyle,
      inputState,
      labelStyleTmp,
      displayOpen,
      datepickerValue,
      dateObject,
      containerStyle,
      placeholder,
    } = this.state;
    const {
      className,
      id,
      labelText,
      infoMessage,
      errorMessage,
      dateParseErrorMessage,
      dayNamesFull,
      monthNamesFull,
      weekStartDay,
      dayNamesShort,
      disablePast,
      minDate,
      maxDate,
      contrast,
    } = this.props;

    const em = inputState === "error" && errorMessage  ||
      inputState === "dateParseError" && dateParseErrorMessage ? `errMsg-${id} ` : "";
    const ariaDescribedby = em + (infoMessage ? `infoMsg-${id}` : "");
    const mainContainerStyles = className
      ? `pe-datepicker-main ${className}`
      : `pe-datepicker-main`;
    const inputStyles = inputStyle
      ? `pe-datepicker-input-styles ${inputStyle}`
      : `pe-datepicker-input-styles`;

    return (
      <div
        className={mainContainerStyles}
        onKeyDown={this.handleKeys}
        onClick={this.datePickerOpen}
        ref={(dom) => (this.container = dom)}
      >
        <label className={labelStyleTmp} htmlFor={id}>
          {labelText}
        </label>

        <div className={containerStyle}>
          <input
            ref={(input) => (this.input = input)}
            type="text"
            id={id}
            placeholder={placeholder}
            value={datepickerValue}
            className={inputStyles}
            aria-describedby={ariaDescribedby}
            aria-invalid={inputState === "error"}
            disabled={inputState === "disabled"}
            readOnly={inputState === "readOnly"}
            onChange={this.changeHandler}
            autoComplete="off"
          />
          <Button className="pe-datepicker-icon" varient="icon">
            <Icon fileName="calendar-18" />
          </Button>
        </div>

        {infoMessage && (
          <span id={`infoMsg-${id}`} className="pe-input--info_message">
            {infoMessage}
          </span>
        )}

        {errorMessage && inputState === "error" && (
          <span id={`errMsg-${id}`} className="pe-input--error_message">
            <Icon fileName="warning-sm-18" isHidden={true} >Error</Icon> {errorMessage}
          </span>
        )}

        {dateParseErrorMessage && inputState === "dateParseError" && (
          <span id={`errMsg-${id}`} className="pe-input--error_message">
            <Icon fileName="warning-sm-18" isHidden={true} >Error</Icon> {dateParseErrorMessage}
          </span>
        )}

        {displayOpen && inputState !== "readOnly" && (
          <div
            ref={(div) => (this.cal = div)}
            onKeyDown={this.focusCheck}
            className="pe-datepicker-calendar-container"
          >
            <Calendar
              contrast={contrast}
              disablePast={disablePast}
              minDate={minDate}
              maxDate={maxDate}
              newSelectedDt={dateObject}
              onSelect={this.calendarHandler}
              dayNamesFull={dayNamesFull}
              monthNamesFull={monthNamesFull}
              weekStartDay={weekStartDay}
              dayNamesShort={dayNamesShort}
            />
          </div>
        )}
      </div>
    );
  }
}

function _datePickerOpen() {
  const { inputState } = this.state;

  if (inputState === "" || inputState === "default" || inputState === "error" || inputState === "dateParseError") {
    this.setState({
      displayOpen: true,
    });
  }
}

function _changeHandler(e) {
  const rawInputValue = 
    typeof e === "string" ? e : e?.target?.value ?? "";  
  
  let dateObj = _parseDateByLocale(rawInputValue, this.props.locale);

  const isValidDate = !isNaN(dateObj.getTime());

  if (this.props.dateParseErrorMessage) {
    this.setState({ inputState: isValidDate ? this.props.inputState : "dateParseError" });
  }

  this.setState({
    datepickerValue: rawInputValue,
    dateObject: isValidDate ? dateObj : null,
    displayOpen: false,
    labelStyleTmp: this.state.labelStyle,
  });
  this.props.changeHandler.call(
    this,
    rawInputValue,
    isValidDate ? dateObj : new Date()
  );
  this.input.focus();
}

function _parseDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);

  if (isNaN(date.getTime())) return "";

  const locale = this.props.locale;
  const group = _getLocaleDateGroup(locale);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  switch (group) {
    case "MDY":
      return `${month}/${day}/${year}`;

    case "DMY":
      return `${day}/${month}/${year}`;

    case "YMD":
      return `${year}-${month}-${day}`;

    default:
      return `${day}/${month}/${year}`;
  }
}

function _datePartsFromMatch (match, group) {
  let year;
  let month;
  let day;

  switch (group) {
    case "MDY":
      month = Number(match[1]);
      day = Number(match[3]);
      year = Number(match[4]);
      break;

    case "DMY":
      day = Number(match[1]);
      month = Number(match[3]);
      year = Number(match[4]);
      break;

    case "YMD":
      year = Number(match[1]);
      month = Number(match[3]);
      day = Number(match[4]);
      break;

    default:
      return null;
  }

  if (
    month < 1 || month > 12 ||
    day < 1 || day > 31
  ) {
    return null;
  }

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day, date };
}

function _parseDateByLocale (value, locale) {
  if (!value) return new Date(NaN);

  const group = _getLocaleDateGroup(locale);
  const regex = DATE_REGEX[group];

  const match = value.match(regex);

  if (!match) return new Date(NaN);

  let parsed = _datePartsFromMatch(match, group);
  if (parsed) {
    return parsed.date;
  }

  // When the primary locale order fails (e.g. month > 12 under MDY), try the
  // other day-month order so mm/dd and dd/mm strings both resolve when unambiguous.
  if (group === "MDY") {
    parsed = _datePartsFromMatch(match, "DMY");
  } else if (group === "DMY") {
    parsed = _datePartsFromMatch(match, "MDY");
  }

  if (parsed) {
    return parsed.date;
  }

  return new Date(NaN);
}

function _getLocaleDateGroup (locale) {
  const normalized = locale?.toLowerCase();

  for (const [group, locales] of Object.entries(DATE_FORMAT_GROUPS)) {
    if (locales.includes(normalized)) {
      return group;
    }
  }

  // Safe default
  return "DMY";
}

function _calendarHandler(date) {
  let dateString = this.parseDate(date.selectedDt);

  this.setState({
    datepickerValue: dateString,
    dateObject: date.selectedDt,
    displayOpen: false,
    labelStyleTmp: this.state.labelStyle,
  });

  this.changeHandler(dateString);
}

function _applyDatePickerStyles(inputState) {
  let { labelStyle, inputStyle, labelFocusStyle, containerStyle } = this.state;

  switch (inputState) {
    case "error":
    case "dateParseError":
      labelStyle = "pe-textLabelInput__label--label_error";
      labelFocusStyle = "pe-textLabelInput__label--label_error";
      inputStyle = "pe-textInput--basic_error";
      containerStyle = "pe-datepicker-container";
      break;
    case "disabled":
      labelStyle = "pe-textLabelInput__label";
      labelFocusStyle = "pe-textLabelInput__label";
      containerStyle = "pe-datepicker-container";
      inputStyle = "pe-textInput--basic";
      break;
    case "readOnly":
      labelStyle = "pe-textLabelInput__label";
      labelFocusStyle = "pe-textLabelInput__label";
      inputStyle = "pe-textInput--input_readonly";
      containerStyle = "pe-datepicker-container";
      break;
    default:
      labelStyle = "pe-textLabelInput__label";
      labelFocusStyle = "pe-textLabelInput__label--label_focus";
      inputStyle = "pe-textInput--basic";
      containerStyle = "pe-datepicker-container";
  }

  this.setState({
    labelStyle,
    labelStyleTmp: labelStyle,
    inputStyle,
    labelFocusStyle,
    containerStyle,
  });
}

DatePicker.propTypes = {
  id: PropTypes.string,
  labelText: PropTypes.string,
  locale: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  infoMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  dateParseErrorMessage: PropTypes.string,
  inputState: PropTypes.string,
  className: PropTypes.string,
  disablePast: PropTypes.bool,
  minDate: PropTypes.object,
  maxDate: PropTypes.object,
  dayNamesFull: PropTypes.arrayOf(PropTypes.string),
  monthNamesFull: PropTypes.arrayOf(PropTypes.string),
  weekStartDay: PropTypes.number,
  dayNamesShort: PropTypes.arrayOf(PropTypes.string),
  datepickerValue: PropTypes.string,
  contrast: PropTypes.bool,
};

DatePicker.defaultProps = {
  locale: "en-us",
  inputState: "",
  labelText: "Select date",
  id: Date.now().toString(),
  contrast: false,
};
